import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ArrowLeft,
  Loader2,
  Upload,
  Image as ImageIcon,
  CheckCircle2,
  ShieldAlert,
  ChevronDown,
} from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import { memberApi as api } from "../lib/memberApi";
import { getAvatarSrc } from "../lib/avatar";
import {
  COUNTRY_PHONE_OPTIONS,
  INDIA_COUNTRY_OPTION,
} from "../data/phoneCountries";
import { getCityOptionsForCountry } from "../data/countryCities";
import { PROFESSION_OPTIONS } from "../data/professionOptions";

const steps = [
  { k: "personal", label: "Personal" },
  { k: "professional", label: "Professional + Photo" },
  { k: "plan", label: "Plan & Review" },
];

const MAX_PHOTO_BYTES = 10 * 1024 * 1024;
const BUSINESS_DESCRIPTION_MAX_LENGTH = 500;
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const PHONE_REGEX = /^\d{5,15}$/;
const INDIAN_PHONE_REGEX = /^[6-9]\d{9}$/;

const PLACEHOLDER_WORDS = new Set([
  "abc",
  "efg",
  "asdf",
  "qwerty",
  "test",
  "demo",
  "sample",
  "fake",
  "dummy",
  "lorem",
  "user",
  "guest",
  "name",
  "email",
  "phone",
  "mobile",
  "member",
  "admin",
  "hello",
]);
const PLACEHOLDER_EMAIL_LOCAL = new Set([
  "test",
  "demo",
  "sample",
  "fake",
  "dummy",
  "asdf",
  "qwerty",
  "user",
  "name",
  "email",
  "member",
  "admin",
]);
const PLACEHOLDER_EMAIL_DOMAIN = new Set([
  "example.com",
  "example.org",
  "example.net",
  "mailinator.com",
  "tempmail.com",
  "10minutemail.com",
  "guerrillamail.com",
  "yopmail.com",
  "fake.com",
  "test.com",
]);
const cleanInput = (value) =>
  (value || "").toString().trim().replace(/\s+/g, " ");
const cleanPhoneDigits = (value) => cleanInput(value).replace(/\D/g, "");
const stripSelectedDialCode = (phone, dial) => {
  const digits = cleanPhoneDigits(phone);
  const dialDigits = cleanPhoneDigits(dial);
  if (!dialDigits || !digits.startsWith(dialDigits)) return digits;
  const withoutDial = digits.slice(dialDigits.length);
  return withoutDial.length >= 5 ? withoutDial : digits;
};
const isPlaceholderName = (value) => {
  const normalized = cleanInput(value).toLowerCase();
  if (!normalized) return true;
  const words = normalized.split(" ");
  return words.some((word) => PLACEHOLDER_WORDS.has(word));
};
const isPlaceholderEmail = (value) => {
  const email = cleanInput(value).toLowerCase();
  if (!EMAIL_REGEX.test(email)) return true;
  const [local, domain] = email.split("@");
  if (!local || !domain) return true;
  if (PLACEHOLDER_EMAIL_LOCAL.has(local)) return true;
  if (PLACEHOLDER_EMAIL_DOMAIN.has(domain)) return true;
  if (local.length < 3) return true;
  return /^(test|demo|sample|fake|dummy|asdf|qwerty)/.test(local);
};
const isPlaceholderPhone = (value) => {
  const phone = cleanPhoneDigits(value);
  if (!PHONE_REGEX.test(phone)) return true;
  return /^(\d)\1+$/.test(phone);
};
const MEMBER_DRAFT_PROFILE_KEY = "ospf_member_draft_profile";
const readMemberDraftProfile = () => {
  try {
    if (typeof window === "undefined") return {};
    const raw = localStorage.getItem(MEMBER_DRAFT_PROFILE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return {};
    return {
      name: cleanInput(parsed.name),
      phone: cleanPhoneDigits(parsed.phone),
      email: cleanInput(parsed.email).toLowerCase(),
      country_code: (parsed.country_code || "IN").toUpperCase(),
      phone_country_code: parsed.phone_country_code || "+91",
      country_name: cleanInput(parsed.country_name) || "India",
    };
  } catch {
    return {};
  }
};
const clearMemberDraftProfile = () => {
  try {
    localStorage.removeItem(MEMBER_DRAFT_PROFILE_KEY);
  } catch { }
};
export default function Membership() {
  const nav = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const photoInputRef = useRef(null);
  const professionInputRef = useRef(null);
  const [photoFile, setPhotoFile] = useState(null);
  const countrySearchRef = useRef(null);
  const citySearchRef = useRef(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [photoPreview, setPhotoPreview] = useState("");
  const [photoMeta, setPhotoMeta] = useState(null);
  const [countryOpen, setCountryOpen] = useState(false);
  const [countrySearch, setCountrySearch] = useState("");
  const [cityOpen, setCityOpen] = useState(false);
  const [citySearch, setCitySearch] = useState("");
  const [pincode, setPincode] = useState("");
  const [fetchingPincode, setFetchingPincode] = useState(false);
  const [initializing, setInitializing] = useState(true);

  const handlePincodeChange = async (e) => {
    const val = e.target.value.replace(/\D/g, "").slice(0, 6);
    setPincode(val);

    if (val.length === 6 && isIndianPlan) {
      setFetchingPincode(true);
      try {
        const res = await fetch(`https://api.postalpincode.in/pincode/${val}`);
        const data = await res.json();
        if (data && data[0] && data[0].Status === "Success") {
          const district = data[0].PostOffice[0].District;
          const state = data[0].PostOffice[0].State;
          const location = `${district}, ${state}`;
          setForm((f) => ({ ...f, city: location }));
          setCitySearch(location);
        } else {
          toast.error("Invalid Pincode");
        }
      } catch (err) {
        console.error(err);
      } finally {
        setFetchingPincode(false);
      }
    }
  };
  const [professionOpen, setProfessionOpen] = useState(false);
  const [professionSearch, setProfessionSearch] = useState("");
  const [customProfession, setCustomProfession] = useState("");
  const [isCustomProfession, setIsCustomProfession] = useState(false);
  const [memberId, setMemberId] = useState(null);
  const draftProfile = readMemberDraftProfile();
  const [selectedCountry, setSelectedCountry] = useState(() => {
    const match = COUNTRY_PHONE_OPTIONS.find(
      (opt) =>
        opt.iso === draftProfile.country_code ||
        opt.dial === draftProfile.phone_country_code,
    );
    return match || INDIA_COUNTRY_OPTION;
  });
  const safeTrim = (v) => (v || "").toString().trim();
  const resolvePhotoSrc = (path) => {
    if (!path) return "";
    return getAvatarSrc({
      id: memberId,
      avatar_path: path,
      has_avatar: true,
    });
  };

  const [form, setForm] = useState({
    name: draftProfile.name || "",
    phone: draftProfile.phone || "",
    email: draftProfile.email || "",

    country_code: draftProfile.country_code || INDIA_COUNTRY_OPTION.iso,
    phone_country_code:
      draftProfile.phone_country_code || INDIA_COUNTRY_OPTION.dial,
    country_name:
      draftProfile.country_name || INDIA_COUNTRY_OPTION.country,
    profession: "",
    business_details: "",
    residence_type:
      draftProfile.country_code === "IN" ? "indian" : "nri",
    city: draftProfile.country_code === "IN" ? "Udaipur" : "",

    avatar_path: "",
    avatar_file_id: "",
  });
  const previewPhotoSrc = photoPreview || resolvePhotoSrc(form.avatar_path);
  const composeBusinessDetails = (source) => {
    const parts = [
      source.business_name,
      source.role,
      source.experience,
      source.business_description,
    ]
      .map(cleanInput)
      .filter(Boolean);
    return parts.join(" | ");
  };
  const change = (k) => (e) =>
    setForm((f) => {
      const next = { ...f, [k]: e.target.value };
      if (
        ["business_name", "role", "experience", "business_description"].includes(
          k,
        )
      ) {
        next.business_details = composeBusinessDetails(next);
      }
      return next;
    });

  const EXPERIENCE_REGEX = /^\d{1,2}$/;
  const handleExperienceChange = (e) => {
    const value = e.target.value.replace(/\D/g, "").slice(0, 2);
    setForm((f) => {
      const next = { ...f, experience: value };
      next.business_details = composeBusinessDetails(next);
      return next;
    });
  };

  const isIndianPlan = selectedCountry.iso === "IN";
  const price = isIndianPlan ? 2100 : 1100;
  const fileRef = useRef(null);
  const isIndianApplicant = selectedCountry.iso === "IN";
  const phoneForSubmit = (value = form.phone) =>
    isIndianApplicant
      ? cleanPhoneDigits(value)
      : stripSelectedDialCode(value, selectedCountry.dial);
  const fullPhoneLabel = (value = form.phone) => {
    const phone = phoneForSubmit(value);
    return phone ? `${selectedCountry.dial} ${phone}` : selectedCountry.dial;
  };
  const canNext = () => {
    if (step === 0) {
      const name = cleanInput(form.name);
      const email = cleanInput(form.email);
      const phone = phoneForSubmit();

      const validPhone = isIndianApplicant
        ? INDIAN_PHONE_REGEX.test(phone)
        : PHONE_REGEX.test(phone);

      return (
        name.length >= 2 &&
        !isPlaceholderName(name) &&
        EMAIL_REGEX.test(email) &&
        !isPlaceholderEmail(email) &&
        validPhone &&
        !isPlaceholderPhone(phone)
      );
    }

    if (step === 1) {
      const professionValue = isCustomProfession
        ? (customProfession || "").trim()
        : safeTrim(form.profession);
      const businessDetailsValue = safeTrim(form.business_details);

      return professionValue?.length >= 2 && businessDetailsValue.length >= 2;
    }

    if (step === 2) {
      return !!form.avatar_path;
    }

    return true;
  };
  const handleContinue = async () => {
    if (!canNext()) return;

    if (step === 0) {
      setStep(1);
      return;
    }

    try {
      const payload = {
        ...form,
        name: cleanInput(form.name),
        email: cleanInput(form.email).toLowerCase(),
        phone: phoneForSubmit(),

        profession: professionValue || form.profession || "",
        business_details:
          safeTrim(form.business_details) || composeBusinessDetails(form),
      };

      const validPhone = isIndianApplicant
        ? INDIAN_PHONE_REGEX.test(payload.phone)
        : PHONE_REGEX.test(payload.phone);

      if (payload.name.length < 2 || isPlaceholderName(payload.name)) {
        toast.error("Please enter a valid name.");
        return;
      }
      if (
        isPlaceholderEmail(payload.email) ||
        !EMAIL_REGEX.test(payload.email)
      ) {
        toast.error("Please enter a real email address, not a placeholder.");
        return;
      }
      if (form.experience && !EXPERIENCE_REGEX.test(form.experience)) {
        toast.error("Experience should be a number with up to 2 digits.");
        return;
      }
      if (isPlaceholderPhone(payload.phone) || !validPhone) {
        toast.error(
          isIndianApplicant
            ? "Please enter a valid 10-digit Indian mobile number."
            : "Please enter a valid phone number with country code.",
        );
        return;
      }

      // If we don't have a memberId yet, CREATE it
      let finalId = memberId;

      if (!memberId) {
        const { data } = await api.post("/membership", payload);
        finalId = data.id;
        setMemberId(finalId);
      } else {
        // If we already have an ID, just update (PATCH) to be safe
        await api.patch(`/membership/${memberId}`, payload);
      }

      // Move to next step
      if (step < 2) {
        setStep((s) => s + 1);
      } else {
        nav(`/checkout/${finalId}`);
      }
    } catch (err) {
      const message = formatApiError(err);
      if (message.includes("String should have at least 2 characters")) {
        toast.error(
          "Please enter your profession and business details before continuing.",
        );
        return;
      }
      if (message.includes("Field required")) {
        toast.error("Please complete all required fields before continuing.");
        return;
      }
      toast.error(message);
    }
  };
  const professionValue = isCustomProfession
    ? safeTrim(customProfession)
    : safeTrim(form.profession);
  const filteredProfessions = useMemo(() => {
    const q = (professionSearch || "").trim().toLowerCase();
    if (!q) return PROFESSION_OPTIONS;
    return PROFESSION_OPTIONS.filter(
      (opt) =>
        opt.value === "__other__" ||
        opt.label.toLowerCase().includes(q) ||
        opt.value.toLowerCase().includes(q),
    );
  }, [professionSearch]);
  const filteredCountries = useMemo(() => {
    const q = (countrySearch || "").trim().toLowerCase();
    if (!q) return COUNTRY_PHONE_OPTIONS;
    return COUNTRY_PHONE_OPTIONS.filter(
      (opt) =>
        opt.country.toLowerCase().includes(q) ||
        opt.iso.toLowerCase().includes(q) ||
        opt.dial.toLowerCase().includes(q),
    ).slice(0, 80);
  }, [countrySearch]);
  const filteredCities = useMemo(() => {
    const q = (citySearch || "").trim().toLowerCase();
    const base = getCityOptionsForCountry(selectedCountry);
    if (!q) return base.slice(0, 40);
    return base.filter((city) => city.toLowerCase().includes(q)).slice(0, 80);
  }, [citySearch, selectedCountry]);

  const chooseCountry = (opt) => {
    setSelectedCountry(opt);
    setForm((f) => ({
      ...f,
      country_code: opt.iso,
      phone_country_code: opt.dial,
      country_name: opt.country,
      residence_type: opt.iso === "IN" ? "indian" : "nri",
      city: opt.iso === "IN" ? "Udaipur" : "",
    }));
    setCountrySearch(`${opt.country} (${opt.dial})`);
    setCitySearch(opt.iso === "IN" ? "Udaipur" : "");
    setCountryOpen(false);
  };

  const toggleCountryDropdown = () => {
    setCountryOpen((open) => {
      if (open) return false;
      setCountrySearch("");
      return true;
    });
    if (!countryOpen) {
      setTimeout(() => countrySearchRef.current?.focus(), 0);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // instant preview
    const localPreview = URL.createObjectURL(file);
    setPhotoPreview(localPreview);

    const formData = new FormData();
    formData.append("file", file);

    try {
      const { data } = await api.post("/membership/photo-upload", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      setForm((prev) => ({
        ...prev,
        avatar_path: data.path,
        avatar_file_id: data.file_id || "",
      }));

      // keep local preview visible
      setPhotoPreview(localPreview);
    } catch (err) {
      console.error(err);
    }
  };

  const onCountrySearchChange = (value) => {
    setCountrySearch(value);
    setCountryOpen(true);
  };

  const chooseCity = (city) => {
    setForm((f) => ({ ...f, city }));
    setCitySearch(city);
    setCityOpen(false);
  };

  const onCitySearchChange = (value) => {
    setCitySearch(value);
    setForm((f) => ({ ...f, city: value }));
    setCityOpen(true);
  };

  const submit = async () => {
    if (submitting) return;

    setSubmitting(true);

    try {
      const payload = {
        ...form,
        name: cleanInput(form.name),
        email: cleanInput(form.email).toLowerCase(),
        phone: phoneForSubmit(),

        profession: professionValue || form.profession || "",
        business_details:
          safeTrim(form.business_details) || composeBusinessDetails(form),
      };

      const validPhone = isIndianApplicant
        ? INDIAN_PHONE_REGEX.test(payload.phone)
        : PHONE_REGEX.test(payload.phone);

      if (payload.name.length < 2 || isPlaceholderName(payload.name)) {
        toast.error("Please enter a valid name.");
        return;
      }
      if (
        isPlaceholderEmail(payload.email) ||
        !EMAIL_REGEX.test(payload.email)
      ) {
        toast.error("Please enter a real email address, not a placeholder.");
        return;
      }
      if (form.experience && !EXPERIENCE_REGEX.test(form.experience)) {
        toast.error("Experience should be a number with up to 2 digits.");
        return;
      }
      if (isPlaceholderPhone(payload.phone) || !validPhone) {
        toast.error(
          isIndianApplicant
            ? "Please enter a valid 10-digit Indian mobile number."
            : "Please enter a valid phone number with country code.",
        );
        return;
      }

      const { data } = await api.post("/membership", payload);

      const finalId = data?.id || memberId || data?.member?.id;

      // EXISTING USER
      if (data?.resume) {
        if (data?.status === "draft") {
          toast.success("Application resumed");
          nav(`/checkout/${finalId}`, {
            state: { member: data.member || payload },
          });
          return;
        }

        if (data?.status === "pending") {
          toast("Your application is under review");
          nav("/member/dashboard");
          return;
        }

        if (data?.status === "approved") {
          nav("/member/dashboard");
          return;
        }
      }

      // NEW USER
      toast.success("Application saved");

      nav(`/checkout/${finalId}`, {
        state: { member: data.member || payload },
      });
    } catch (e) {
      const message = formatApiError(e);
      if (message.includes("String should have at least 2 characters")) {
        toast.error(
          "Please enter your profession and business details before submitting.",
        );
        return;
      }
      if (message.includes("Field required")) {
        toast.error("Please complete all required fields before submitting.");
        return;
      }
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      if (photoPreview) URL.revokeObjectURL(photoPreview);
    };
  }, [photoPreview]);

  useEffect(() => {
    const currentCity = form.city || "";
    setCitySearch(currentCity);
  }, [form.city]);

  const validateImage = (file) =>
    new Promise((resolve, reject) => {
      const previewUrl = URL.createObjectURL(file);
      const img = new Image();
      img.onload = () => {
        const dims = {
          width: img.naturalWidth,
          height: img.naturalHeight,
          previewUrl,
        };
        URL.revokeObjectURL(previewUrl);
        resolve(dims);
      };
      img.onerror = () => {
        URL.revokeObjectURL(previewUrl);
        reject(new Error("Could not read image dimensions."));
      };
      img.src = previewUrl;
    });

  const onPickPhoto = () => photoInputRef.current?.click();

  const onPhotoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file.");
      e.target.value = "";
      return;
    }
    if (file.size > MAX_PHOTO_BYTES) {
      toast.error("Photo must be 10 MB or smaller.");
      e.target.value = "";
      return;
    }

    setUploadingPhoto(true);
    try {
      const dims = await validateImage(file);
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/membership/photo-upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      setForm((f) => ({ ...f, avatar_path: data.path, avatar_file_id: data.file_id || "" }));
      setPhotoMeta({
        filename: data.filename || file.name,
        width: data.width,
        height: data.height,
      });
      setPhotoPreview((prev) => {
        if (prev) URL.revokeObjectURL(prev);
        return URL.createObjectURL(file);
      });
      toast.success("Professional photo uploaded.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploadingPhoto(false);
      e.target.value = "";
    }
  };

  const removePhoto = () => {
    setForm((f) => ({ ...f, avatar_path: "", avatar_file_id: "" }));
    setPhotoMeta(null);
    setPhotoPreview((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return "";
    });
  };

  const chooseProfession = (opt) => {
    if (opt.value === "__other__") {
      setIsCustomProfession(true);
      setProfessionOpen(false);
      setProfessionSearch(customProfession || "");
      setForm((f) => ({ ...f, profession: customProfession.trim() }));
      setTimeout(() => professionInputRef.current?.focus(), 0);
      return;
    }
    setIsCustomProfession(false);
    setCustomProfession("");
    setProfessionSearch(opt.label);
    setForm((f) => ({ ...f, profession: opt.value }));
    setProfessionOpen(false);
  };

  const onProfessionSearchChange = (value) => {
    setProfessionSearch(value);
    setProfessionOpen(true);
    const matched = PROFESSION_OPTIONS.find(
      (opt) =>
        opt.value === value ||
        opt.label.toLowerCase() === value.trim().toLowerCase(),
    );
    if (matched && matched.value !== "__other__") {
      setIsCustomProfession(false);
      setForm((f) => ({ ...f, profession: matched.value }));
    } else if (!isCustomProfession) {
      setForm((f) => ({ ...f, profession: value }));
    }
  };

  // Add uploadingPhoto to the dependency array if you want to pause saving during uploads
  useEffect(() => {
    if (!memberId || step === 0 || uploadingPhoto) return;

    const save = async () => {
      try {
        await api.patch(`/membership/${memberId}`, form);
      } catch (e) {
        console.error("Auto-save failed", e);
      }
    };

    const timeout = setTimeout(save, 1000);
    return () => clearTimeout(timeout);
  }, [form, memberId, step, uploadingPhoto]);

  useEffect(() => {
    const load = async () => {
      const token = localStorage.getItem("ospf_member_token");
      if (!token) {
        setInitializing(false);
        return;
      }
      try {
        const { data } = await api.get("/members/me");
        const member = data.member || data;

        if (member && member.id) {
          setForm((prev) => ({ ...prev, ...member }));
          if (member.phone_country_code || member.country_code) {
            const country = COUNTRY_PHONE_OPTIONS.find(
              (opt) =>
                opt.dial === member.phone_country_code ||
                opt.iso === member.country_code,
            );
            if (country) {
              setSelectedCountry(country);
              setCountrySearch(`${country.country} (${country.dial})`);
            }
          }
          setMemberId(member.id);

          // Only redirect members who have already moved past the form.
          // Draft members should stay here so they can complete their profile.
          if (member.membership_status === "approved") {
            clearMemberDraftProfile();
            nav("/member/dashboard", { replace: true });
          }
        }
      } catch (e) {
        // It's okay if this fails; it just means they are a new visitor
      } finally {
        setInitializing(false);
      }
    };
    load();
  }, [nav]); // No 'form' dependency here

  const handleChange = async (data) => {
    setForm(data);

    await api.patch(`/membership/${memberId}`, data);
  };

  const onLogin = async () => {
    try {
      const { data } = await api.get("/members/me");

      const member = data.member || data;

      if (member.membership_status === "draft") {
        nav(`/membership?resume=${member.id}`);
      } else {
        nav("/member/dashboard");
      }
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setCountryOpen(false);
        setCityOpen(false);
        setProfessionOpen(false);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream">
        <Loader2 className="w-8 h-8 animate-spin text-brand" />
      </div>
    );
  }

  return (
    <div
      className="container-px pt-16 md:pt-24 pb-24"
      data-testid="membership-page"
    >
      <div className="max-w-2xl mx-auto">
        <div className="overline mb-4 text-center">Apply for membership</div>
        <h1 className="font-heading text-4xl md:text-5xl font-light text-ink text-center leading-tight">
          Become part of the forum.
        </h1>

        {/* Step indicator */}
        <div
          className="mt-12 flex items-center justify-between gap-3"
          data-testid="membership-stepper"
        >
          {steps.map((s, i) => (
            <div key={s.k} className="flex-1 flex items-center gap-3">
              <div
                className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-medium transition ${i <= step ? "bg-brand text-white" : "bg-white border border-black/10 text-ink-muted"}`}
              >
                {i + 1}
              </div>
              <div
                className={`text-sm font-medium ${i === step ? "text-ink" : "text-ink-muted"}`}
              >
                {s.label}
              </div>
              {i < steps.length - 1 && (
                <div
                  className={`flex-1 h-px ${i < step ? "bg-brand" : "bg-black/10"}`}
                />
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 card-premium">
          {step === 0 && (
            <div className="space-y-5" data-testid="membership-step-personal">
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">
                  Full name
                </label>
                <input
                  className="input-premium"
                  required
                  value={form.name}
                  onChange={change("name")}
                  placeholder="Full Name"
                  data-testid="membership-name-input"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-2">
                  Country / residence
                </label>
                <div className="relative">
                  <button
                    type="button"
                    className="input-premium w-full h-14 px-4 text-left flex items-center justify-between gap-3 overflow-hidden"
                    onClick={toggleCountryDropdown}
                    data-testid="membership-country-btn"
                  >
                    <span className="min-w-0 flex-1 truncate">
                      {selectedCountry.country} · {selectedCountry.dial} ·{" "}
                      {selectedCountry.iso === "IN" ? "India" : "NRI"}
                    </span>
                    <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
                  </button>
                  {countryOpen && (
                    <div className="absolute left-0 z-30 mt-2 w-[min(92vw,420px)] rounded-2xl border border-black/10 bg-white shadow-premium overflow-hidden">
                      <div className="p-3 border-b border-black/5">
                        <input
                          ref={countrySearchRef}
                          className="input-premium"
                          value={countrySearch}
                          onChange={(e) => onCountrySearchChange(e.target.value)}
                          placeholder="Search country or dial code"
                          data-testid="membership-country-search"
                        />
                      </div>
                      <div className="max-h-72 overflow-y-auto">
                        {filteredCountries.length > 0 ? (
                          filteredCountries.map((opt) => (
                            <button
                              key={`${opt.iso}-${opt.dial}`}
                              type="button"
                              onClick={() => chooseCountry(opt)}
                              className="w-full px-4 py-3 text-left hover:bg-cream-deep transition flex items-center justify-between gap-3"
                              data-testid={`country-option-${opt.iso.toLowerCase()}`}
                            >
                              <span className="text-ink">
                                {opt.country}
                              </span>
                              <span className="text-sm text-ink-muted">
                                {opt.dial}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="px-4 py-3 text-sm text-ink-muted">
                            No country found.
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                <p className="mt-2 text-xs text-ink-muted">
                  Choose India or NRI. All countries are searchable.
                </p>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-2">
                    Phone country code + number
                  </label>
                  <div className="grid grid-cols-1 md:grid-cols-[108px_minmax(0,1fr)] gap-3 items-stretch">
                    <div className="relative md:w-[108px]">
                      <button
                        type="button"
                        className="input-premium w-full h-14 px-3 text-sm md:text-[14px] text-left flex items-center justify-between gap-2 overflow-hidden"
                        onClick={toggleCountryDropdown}
                        data-testid="membership-country-code-btn"
                      >
                        <span className="truncate font-semibold">
                          {selectedCountry.dial}
                        </span>
                        <ChevronDown className="h-4 w-4 shrink-0 text-ink-muted" />
                      </button>
                    </div>
                    <div className="min-w-0">
                      <input
                        type="tel"
                        inputMode="tel"
                        maxLength={isIndianApplicant ? 10 : 24}
                        className="input-premium"
                        value={form.phone}
                        onChange={(e) => {
                          const val = isIndianApplicant
                            ? e.target.value.replace(/\D/g, "")
                            : e.target.value.replace(/[^\d+\s().-]/g, "");
                          setForm((f) => ({ ...f, phone: val }));
                        }}
                        placeholder={
                          isIndianApplicant
                            ? "Enter 10-digit mobile number"
                            : "Enter phone number with ISD if needed"
                        }
                        required
                        pattern={
                          isIndianApplicant ? "[6-9][0-9]{9}" : "[+0-9 ()-]{5,24}"
                        }
                        title={
                          isIndianApplicant
                            ? "Enter a valid 10-digit Indian mobile number"
                            : "Enter a valid phone number. You may include ISD code, spaces or hyphens."
                        }
                      />
                    </div>
                  </div>
                  <p className="mt-2 text-xs text-ink-muted">
                    {isIndianApplicant
                      ? "Indian mobile number, 10 digits."
                      : "NRI numbers can include ISD/country code, spaces, hyphens, or brackets."}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-ink-soft mb-2">
                    Email
                  </label>
                  <input
                    className="input-premium h-14 px-4 text-base md:text-lg w-full"
                    type="email"
                    value={form.email}
                    onChange={change("email")}
                    placeholder="name@gmail.com"
                    pattern={EMAIL_REGEX.source}
                    title="Enter a valid email address"
                    required
                    data-testid="membership-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {isIndianPlan && (
                  <div>
                    <label className="block text-sm font-medium text-ink-soft mb-2">
                      Pincode
                    </label>
                    <div className="relative">
                      <input
                        className="input-premium"
                        value={pincode}
                        onChange={handlePincodeChange}
                        placeholder="e.g. 313001"
                        maxLength={6}
                        inputMode="numeric"
                      />
                      {fetchingPincode && (
                        <div className="absolute right-3 top-1/2 -translate-y-1/2">
                          <Loader2 className="w-4 h-4 animate-spin text-brand" />
                        </div>
                      )}
                    </div>
                  </div>
                )}
                <div className={isIndianPlan ? "" : "md:col-span-2"}>
                  <label className="block text-sm font-medium text-ink-soft mb-2">
                    City / location
                  </label>
                  <div className="relative">
                    <input
                      ref={citySearchRef}
                      className="input-premium"
                      value={citySearch}
                      onFocus={() => setCityOpen(true)}
                      onChange={(e) => onCitySearchChange(e.target.value)}
                      placeholder={
                        isIndianPlan
                          ? "Enter your City"
                          : "Type any city, state, or location"
                      }
                      data-testid="membership-city-input"
                    />
                    {cityOpen && (
                      <div className="absolute z-20 mt-2 w-full rounded-2xl border border-black/10 bg-white shadow-premium overflow-hidden">
                        <div className="max-h-72 overflow-y-auto">
                          {filteredCities.length > 0 ? (
                            filteredCities.map((city) => (
                              <button
                                key={city}
                                type="button"
                                onClick={() => chooseCity(city)}
                                className="w-full px-4 py-3 text-left hover:bg-cream-deep transition"
                                data-testid={`city-option-${city.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                              >
                                {city}
                              </button>
                            ))
                          ) : (
                            <div className="px-4 py-3 text-sm text-ink-muted">
                              No city matches. Keep typing your own city.
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <p className="mt-2 text-xs text-ink-muted">
                    India cities are searchable. If you are outside India, just
                    select from that country's city list or type your own city.
                  </p>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div
              className="space-y-5"
              data-testid="membership-step-professional"
            >
              <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                <div className="lg:col-span-3 space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-ink-soft mb-2">
                      Profession / designation
                    </label>
                    <div className="relative">
                      <input
                        ref={professionInputRef}
                        className="input-premium"
                        value={professionSearch}
                        onFocus={() => setProfessionOpen(true)}
                        onChange={(e) =>
                          onProfessionSearchChange(e.target.value)
                        }
                        placeholder="Search profession, course, service, or role"
                        data-testid="membership-profession-search"
                      />
                      {professionOpen && (
                        <div className="absolute z-20 mt-2 w-full rounded-2xl border border-black/10 bg-white shadow-premium overflow-hidden">
                          <div className="max-h-72 overflow-y-auto">
                            {filteredProfessions.length > 0 ? (
                              filteredProfessions.map((opt) => (
                                <button
                                  key={opt.value}
                                  type="button"
                                  onClick={() => chooseProfession(opt)}
                                  className="w-full text-left px-4 py-3 hover:bg-cream-deep transition flex items-center justify-between gap-3"
                                  data-testid={`profession-option-${opt.value.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                                >
                                  <span className="text-ink">{opt.label}</span>
                                  {opt.value === "__other__" ? (
                                    <span className="text-xs text-brand-secondary uppercase tracking-[0.18em]">
                                      Custom
                                    </span>
                                  ) : null}
                                </button>
                              ))
                            ) : (
                              <div className="px-4 py-3 text-sm text-ink-muted">
                                No matches. Choose Other to type manually.
                              </div>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                    <p className="mt-2 text-xs text-ink-muted">
                      Search across CA, CS, CFA, MBA, BTech, MBBS, law, IAS/IPS,
                      scientist, ISRO/DRDO, finance, biotech, and more.
                    </p>
                    {isCustomProfession && (
                      <div className="mt-4">
                        <label className="block text-sm font-medium text-ink-soft mb-2">
                          Enter profession / course
                        </label>
                        <input
                          className="input-premium"
                          ref={professionInputRef}
                          value={customProfession}
                          onChange={(e) => {
                            setCustomProfession(e.target.value);
                            setForm((f) => ({
                              ...f,
                              profession: e.target.value,
                            }));
                          }}
                          placeholder="Type any profession or course"
                          data-testid="membership-profession-custom-input"
                        />
                      </div>
                    )}
                  </div>
                  <div className="space-y-4">
                    {/* Business Name */}
                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Business / Firm Name
                      </label>
                      <input
                        className="input-premium"
                        value={form.business_name || ""}
                        onChange={change("business_name")}
                        placeholder="e.g. ParshWebCraft"
                      />
                    </div>

                    {/* Role */}
                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Role / Position
                      </label>
                      <input
                        className="input-premium"
                        value={form.role || ""}
                        onChange={change("role")}
                        placeholder="e.g. Founder, CA, Doctor"
                      />
                    </div>

                    {/* Experience */}
                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Experience (in years)
                      </label>
                      <input
                        className="input-premium"
                        value={form.experience || ""}
                        onChange={handleExperienceChange}
                        placeholder="e.g. 5 "
                      />
                    </div>
                    {/* Description (Optional) */}
                    <div>
                      <label className="block text-sm font-medium text-ink-soft mb-2">
                        Description{" "}
                        <span className="text-ink-muted">(optional)</span>
                      </label>
                      <textarea
                        className="input-premium min-h-[100px]"
                        value={form.business_description || ""}
                        onChange={change("business_description")}
                        placeholder="Short description about your work, services, or expertise"
                        maxLength={BUSINESS_DESCRIPTION_MAX_LENGTH}
                      />
                      <div className="mt-2 flex items-center justify-between text-[11px] text-ink-muted">
                        <span>Keep it short and clear</span>
                        <span>
                          {(form.business_description || "").length}/
                          {BUSINESS_DESCRIPTION_MAX_LENGTH}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="lg:col-span-2">
                  <div className="rounded-2xl border border-dashed border-brand/30 bg-brand/5 p-5 h-full">
                    <div className="flex items-start gap-3">
                      <div className="w-11 h-11 rounded-xl bg-white text-brand flex items-center justify-center shadow-sm shrink-0">
                        <ImageIcon className="w-5 h-5" strokeWidth={1.6} />
                      </div>
                      <div>
                        <div className="font-heading text-lg text-ink">
                          Professional photo
                        </div>
                        <p className="text-sm text-ink-soft mt-1">
                          Upload any clear image. The full image will be shown
                          in profile blocks. Up to 10 MB.
                        </p>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploadingPhoto}
                      className="btn-secondary mt-5 w-full"
                      data-testid="membership-photo-upload-btn"
                    >
                      {uploadingPhoto ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />{" "}
                          Uploading…
                        </>
                      ) : (
                        "Upload photo"
                      )}
                    </button>

                    {previewPhotoSrc ? (
                      <div className="mt-4 space-y-3">
                        <div className="rounded-2xl overflow-hidden border border-black/5 bg-white">
                          <img
                            src={previewPhotoSrc}
                            alt="Professional preview"
                            className="w-full h-56 object-contain bg-white"
                            data-testid="membership-photo-preview"
                          />
                        </div>
                        <div className="flex items-start gap-2 text-xs text-ink-muted">
                          <CheckCircle2 className="w-4 h-4 text-green-600 shrink-0 mt-0.5" />
                          <div>
                            <div className="text-ink-soft font-medium">
                              {photoMeta?.filename || "Photo uploaded"}
                            </div>
                            <div>
                              {photoMeta
                                ? `${photoMeta.width} × ${photoMeta.height} pixels`
                                : "HD photo received"}
                            </div>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={removePhoto}
                          className="btn-ghost w-full"
                          data-testid="membership-photo-remove-btn"
                        >
                          Remove photo
                        </button>
                      </div>
                    ) : (
                      <div className="mt-4 rounded-2xl bg-white/80 border border-black/5 p-4 text-sm text-ink-muted">
                        <div className="flex items-center gap-2 text-ink-soft">
                          <ShieldAlert className="w-4 h-4 text-amber-600" />
                          <span>
                            This photo is mandatory before you can continue.
                          </span>
                        </div>
                        <div className="mt-2">
                          It will be used for your member record, directory
                          profile, and admin review.
                        </div>
                      </div>
                    )}
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileRef}
                      className="hidden"
                      onChange={onPhotoFile}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-6" data-testid="membership-step-plan">
              <div>
                <label className="block text-sm font-medium text-ink-soft mb-3">
                  Mobile country code
                </label>
                <div
                  className={`rounded-2xl p-5 border transition ${isIndianPlan ? "border-brand bg-brand/5" : "border-brand bg-brand/5"}`}
                >
                  <div className="flex items-baseline justify-between gap-4">
                    <span className="font-heading text-lg font-medium text-ink">
                      {isIndianPlan ? "Indian Resident" : "NRI / Overseas"}
                    </span>
                    <span className="font-heading text-2xl font-medium text-brand">
                      ₹{price}
                    </span>
                  </div>
                </div>
                <p className="mt-3 text-sm text-ink-muted">
                  Current selection: {selectedCountry.country} (
                  {selectedCountry.dial}) and the plan is automatically set to{" "}
                  {isIndianPlan ? "Indian Resident" : "NRI / Overseas"}.
                </p>
              </div>

              <div
                className="rounded-2xl bg-cream-deep p-5 text-sm space-y-2"
                data-testid="membership-review"
              >
                <div className="overline mb-2">Review</div>
                <Row k="Name" v={form.name} />
                <Row k="Email" v={form.email} />

                <Row
                  k="Country code"
                  v={`${selectedCountry.country} (${selectedCountry.dial})`}
                />
                <Row k="Phone" v={fullPhoneLabel()} />
                <Row k="Profession" v={professionValue || "—"} />
                <Row k="City" v={form.city || "—"} />
                <Row
                  k="Photo"
                  v={
                    previewPhotoSrc ? (
                      <img
                        src={previewPhotoSrc}
                        className="w-16 h-16 rounded object-contain bg-white"
                        alt="Selected photo preview"
                      />
                    ) : (
                      "Please upload your photo to continue"
                    )
                  }
                />
                <Row
                  k="Plan"
                  v={`${isIndianPlan ? "Indian Resident" : "NRI"} · ₹${price}`}
                />
              </div>
            </div>
          )}

          <div className="mt-8 flex items-center justify-between">
            <button
              type="button"
              onClick={() => setStep((s) => Math.max(0, s - 1))}
              disabled={step === 0}
              className="btn-ghost disabled:opacity-40"
              data-testid="membership-back-btn"
            >
              <ArrowLeft className="w-4 h-4" /> Back
            </button>

            {step < steps.length - 1 ? (
              <button
                type="button"
                onClick={handleContinue}
                disabled={!canNext()}
                className="btn-primary"
              >
                Continue
              </button>
            ) : (
              <button
                type="button"
                onClick={submit}
                disabled={submitting}
                className="btn-primary"
                data-testid="membership-submit-btn"
              >
                {submitting ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" /> Submitting…
                  </>
                ) : (
                  <>
                    Save & continue <ArrowRight className="w-4 h-4" />
                  </>
                )}
              </button>
            )}
          </div>
        </div>

        <p className="mt-6 text-xs text-ink-muted text-center">
          By applying, you agree to abide by the forum's bye-laws. Your
          application will be reviewed by the council within 7 working days.
        </p>
      </div>
    </div>
  );
}

function Row({ k, v }) {
  return (
    <div className="flex items-start justify-between gap-4">
      <span className="text-ink-muted">{k}</span>
      <span className="text-ink text-right font-medium">{v}</span>
    </div>
  );
}
